import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, tap, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { trackPorCodigo, trackPorId } from 'src/app/services/odoo/odoo-maestros-formato';
import {
  OdooEstadoUi, OdooFilaMaestro, OdooListaFiltro, OdooMovimiento, OdooPaginaLista,
} from 'src/app/services/odoo/odoo-maestros.models';
import { OdooMaestrosService } from 'src/app/services/odoo/odoo-maestros.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { OdooCarga, OdooConteos, PERMISO_ODOO_ESCRIBIR } from 'src/app/services/odoo/odoo.models';
import { conCarga, mensajeDeFallo, OdooService } from 'src/app/services/odoo/odoo.service';

const fNumero = new Intl.NumberFormat('es-VE');

/**
 * PESTAÑA CLIENTES. Búsqueda con espera de 300 ms, filtro Estado, paginación en el servidor.
 * Al tocar una fila se abre un panel a la derecha con Detalle e Historial del cliente.
 */
@Component({
  selector: 'app-odoo-clientes',
  templateUrl: './odoo-clientes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooClientesComponent implements OnDestroy {
  readonly limite = 20;
  filtro: OdooListaFiltro = { q: '', estado: '', comercio: '', page: 1, limit: this.limite };
  readonly buscar = new FormControl('');
  readonly estados: { valor: OdooEstadoUi | ''; texto: string }[] = [
    { valor: '', texto: 'Todos' },
    { valor: 'anotado', texto: 'Anotado' },
    { valor: 'esperando', texto: 'Esperando envío' },
    { valor: 'problema', texto: 'Con problema' },
  ];
  readonly esperaSkeleton = [0, 1, 2, 3, 4];
  seleccionado: OdooFilaMaestro | null = null;
  pestana: 'detalle' | 'historial' = 'detalle';
  /** _id del movimiento abierto en el Historial (se ve el mismo detalle que en Bitácora). */
  abierto: string | null = null;

  private readonly params$ = new BehaviorSubject<OdooListaFiltro>(this.filtro);
  private readonly sel$ = new BehaviorSubject<string | null>(null);
  readonly lista$: Observable<OdooCarga<OdooPaginaLista<OdooFilaMaestro>>> = conCarga(this.params$, (f) => this.api.clientes(f)).pipe(
    tap((v) => this.sincronizar(v.datos?.items)),
  );
  private readonly refresco$ = new BehaviorSubject<void>(undefined);
  /** Cifras de clientes (anotados, esperando, con problema); si el resumen falla, la franja se oculta. */
  readonly conteos$: Observable<OdooConteos | null> = this.refresco$.pipe(
    switchMap(() => this.odoo.resumen().pipe(map((r) => r.maestros.por_tipo.cliente), catchError(() => of(null)))),
  );
  enviando = false;
  readonly movimientos$: Observable<OdooCarga<OdooMovimiento[]>> = conCarga(
    this.sel$.pipe(filter((id): id is string => !!id)), (id) => this.api.movimientosCliente(id, 30));
  readonly trackFila = trackPorCodigo;
  readonly porIndice = (i: number): number => i;
  readonly porValor = (_: number, e: { valor: string }): string => e.valor;
  readonly esperaMov = [0, 1];
  readonly trackMov = trackPorId;
  private readonly sub: Subscription;

  constructor(
    private api: OdooMaestrosService, private odoo: OdooService, public _auth: AuthService,
    private toast: OdooToastService, private cdr: ChangeDetectorRef,
  ) {
    this.sub = this.buscar.valueChanges.pipe(
      map((v) => String(v || '').trim()), debounceTime(300), distinctUntilChanged(),
    ).subscribe((q) => this.cambiar({ q, page: 1 }));
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }

  /** Si el panel está abierto, lo pone al día con la fila recién traída de la lista. */
  private sincronizar(items: OdooFilaMaestro[] | undefined): void {
    const id = this.seleccionado?.source_id;
    const nuevo = id && items ? items.find((x) => x.source_id === id) : null;
    if (nuevo) { this.seleccionado = nuevo; this.cdr.markForCheck(); }
  }

  private cambiar(parcial: Partial<OdooListaFiltro>): void {
    this.filtro = { ...this.filtro, ...parcial };
    this.params$.next(this.filtro);
  }

  alternar(id: string): void { this.abierto = this.abierto === id ? null : id; }
  verTodo(): void { this.pestana = 'historial'; }
  elegirEstado(valor: string): void { this.cambiar({ estado: valor as OdooEstadoUi | '', page: 1 }); }
  pagina(n: number): void { this.cambiar({ page: n }); }
  recargar(): void { this.params$.next({ ...this.filtro }); }

  numero(n: number): string { return fNumero.format(n); }
  /** Solo la vista: quita la secuencia literal «\n» y los saltos o espacios sobrantes del nombre. */
  limpiar(n: string | null | undefined): string { return (n || '').replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim(); }
  get puedeEditar(): boolean { return this._auth.hasPermission(PERMISO_ODOO_ESCRIBIR); }
  total(k: OdooConteos): number { return k.anotados + k.pendientes + k.errores; }
  cerrar(): void { this.seleccionado = null; }

  /** Vuelve a enviar este cliente a Odoo; al terminar avisa, refresca lista, cifras y movimientos. */
  reenviar(): void {
    const c = this.seleccionado;
    if (!c || this.enviando || !this.puedeEditar) return;
    this.enviando = true;
    this.odoo.reenviar('cliente', c.source_id).subscribe({
      next: (r) => {
        this.enviando = false;
        this.toast.mostrar(r.estado === 'error' ? (r.ultimo_error || 'Odoo no aceptó el envío.') : 'Cliente enviado a Odoo.', r.estado !== 'error');
        this.refrescar(c.source_id);
      },
      error: (e: unknown) => { this.enviando = false; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  private refrescar(id: string): void {
    this.recargar();
    this.refresco$.next();
    this.sel$.next(id);
    this.cdr.markForCheck();
  }

  elegir(f: OdooFilaMaestro): void {
    if (this.seleccionado?.source_id === f.source_id) return;
    this.seleccionado = f;
    this.pestana = 'detalle';
    this.abierto = null;
    this.sel$.next(f.source_id);
  }
}
